import React from "react";
import { Lock, ShieldCheck, KeyRound, FileCheck2, Server, Eye, ShieldOff, ListChecks } from "lucide-react";
import { useI18n } from "../../i18n";
import { Container, Section, Card, Badge } from "../../components/ui/Primitives";
import { Button } from "../../components/ui/Button";
import { PageHero, CTASection } from "../../components/marketing/Shared";

const PILLARS = [
  { icon: Lock, title: "Encryption everywhere", desc: "TLS 1.3 in transit and AES-256 at rest for captures, biometric scores and results." },
  { icon: KeyRound, title: "One-time code, server-to-server", desc: "The browser only carries a single-use code; the verdict is fetched over an authenticated backend channel, never forgeable from the redirect." },
  { icon: ShieldOff, title: "Immutable terminal verdicts", desc: "A session leaves pending once — passed, failed or expired — and can never be flipped or reopened." },
  { icon: Eye, title: "Clear consent & recording notice", desc: "Every user sees an explicit, logged consent and recording notice on the hosted verification page." },
  { icon: Server, title: "Per-tenant isolation", desc: "Every session, capture and result is scoped to its client app (tenant); IP allowlist and rate limits per API token." },
  { icon: ListChecks, title: "Full audit trail", desc: "Every API call and verdict is logged, with a per-token IP allowlist and rate limits." },
];

const CERTS = [
  { label: "End-to-end encryption", tone: "success" },
  { label: "Immutable verdicts", tone: "success" },
  { label: "Per-tenant isolation", tone: "success" },
  { label: "DPA available", tone: "brand" },
];

export default function Security() {
  const { t } = useI18n();
  return (
    <>
      <PageHero
        breadcrumb={t("nav.security")}
        eyebrow={t("security.hero.eyebrow")}
        title={t("security.hero.title")}
        lead={t("security.hero.lead")}
      >
        <div className="flex flex-wrap gap-2">
          {CERTS.map((c) => (
            <Badge key={c.label} tone={c.tone} className="px-3 py-1">
              <ShieldCheck className="h-3.5 w-3.5" /> {c.label}
            </Badge>
          ))}
        </div>
      </PageHero>

      <Section>
        <Container>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {PILLARS.map((p) => (
              <Card key={p.title} className="p-6">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-success/12 text-success">
                  <p.icon className="h-5 w-5" />
                </span>
                <h3 className="mt-4 text-base font-bold">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.desc}</p>
              </Card>
            ))}
          </div>
        </Container>
      </Section>

      <Section alt>
        <Container>
          <Card className="mx-auto flex max-w-3xl flex-col items-center gap-4 p-8 text-center">
            <FileCheck2 className="h-10 w-10 text-brand" />
            <h2 className="text-2xl">Data Processing Agreement</h2>
            <p className="max-w-xl text-muted-foreground">
              We provide a standard DPA covering roles, sub-processors, biometric-data handling, retention
              and data-subject rights. Enterprise customers can request custom terms and a security questionnaire.
            </p>
            <Button to="/contato">{t("common.contactSales")}</Button>
          </Card>
        </Container>
      </Section>

      <CTASection />
    </>
  );
}
